import React, { useState, useMemo, useCallback } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/plugins/counter.css';

import { Box, Paper, useMediaQuery, useTheme, Typography } from '@mui/material';
import AttachAvatar from '../AttachAvatar';
import Carousel from 'react-material-ui-carousel';

const AttachmentGallery = ({ attachments = [], defaultimg, isDetailScreen }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [openPhoto, setOpenPhoto] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoMimeTypes = {
  mp4: 'video/mp4',
  mkv: 'video/x-matroska',
  webm: 'video/webm',
  mov: 'video/mp4',
  quicktime: 'video/mp4',
  avi: 'video/x-msvideo',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
  '3gp': 'video/3gpp',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
  hevc: 'video/H265', // not standardized, fallback below is safer
  heic: 'image/heic', // if HEIC is used as image
};

  const getFullUrl = (att) =>
    att?.startsWith('http') ? att : `${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${att}`;

  // const slides = useMemo(
  //   () =>
  //     attachments.map((att) => {
  //       const url = getFullUrl(att);
  //       const isVideo = /\.(mp4|webm|ogg)$/i.test(url);
  //       return isVideo
  //         ? {
  //             type: 'video',
  //             sources: [
  //               {
  //                 src: url,
  //                 type: 'video/mp4',
  //               },
  //             ],
  //             poster: '', // You can set preview thumbnail here if available
  //           }
  //         : { src: url };
  //     }),
  //   [attachments]
  // );

  const slides = useMemo(() =>
  attachments.map((att) => {
    const url = getFullUrl(att);
    const extensionMatch = url.match(/\.(\w+)(\?.*)?$/i);
    const extension = extensionMatch?.[1]?.toLowerCase();

    const mimeType = videoMimeTypes[extension];

    const isVideoOrAudio = !!mimeType && (mimeType.startsWith('video/') || mimeType.startsWith('audio/'));
    console.log('mimeType', mimeType)
    if (isVideoOrAudio) {
      return {
        type: 'video',
        sources: [
          {
            src: url,
            type: mimeType || 'application/octet-stream',
          },
        ],
        poster: '', // optional: set a preview image
      };
    }

    // fallback to image
    return { src: url };
  }), [attachments]
);

  const handleOpenDialog = useCallback((index) => {
    setCurrentIndex(index);
    setOpenPhoto(true);
  }, []);

  const renderMediaItem = (attachment, index) => (
    <AttachAvatar
      key={index}
      src={attachment}
      autoPlay
      muted
      alt="Media"
      variant={isSm ? 'rounded' : 'square'}
      className="attachment-avatar"
      sx={{
        width: '100%',
        height: isSm ? '40vh' : '40vh',
        objectFit: isSm ? 'contain' : 'cover',
        cursor: 'pointer',
      }}
      onClick={() => handleOpenDialog(index)}
      onHandleLightBox={() => handleOpenDialog(index)}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = defaultimg;
      }}
      isDetailScreen={isDetailScreen}
    />
  );

  const renderSingleAttachment = () =>
    isSm ? (
      renderMediaItem(attachments[0], 0)
    ) : (
      <Paper
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 1,
          borderColor: 'divider',
          boxShadow: 'none',
          padding: '10px',
        }}
      >
        {renderMediaItem(attachments[0], 0)}
      </Paper>
    );

  const renderMultipleAttachments = () => {
    const slidesMarkup = attachments.map((attachment, index) => {
      return (
        <Box
          key={index}
          sx={{
            height: isSm ? '40vh' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {renderMediaItem(attachment, index)}
          <Box
            sx={{
              zIndex: 1000,
              position: 'absolute',
              bottom: 0,
              right: '1%',
              background: 'rgba(0,0,0,0.7)',
              padding: '5px 10px',
              borderRadius: '3px',
            }}
          >
            <Typography sx={{ fontSize: '12px', color: 'white' }}>
              {index + 1}/{attachments.length} Media
            </Typography>
          </Box>
        </Box>
      );
    });

    return (
      <Carousel
        autoPlay={false}
        indicators
        navButtonsAlwaysInvisible={false}
        sx={{
          height: '40vh',
          width: '100%',
        }}
        renderIndicator={({ index }) => (
          <Box
            sx={{
              zIndex: 1000,
              bottom: 0,
              right: 0,
              background: 'red',
              color: 'black',
              padding: '5px 10px',
              borderRadius: '3px',
            }}
          >
            {index + 1}/{attachments.length} media
          </Box>
        )}
      >
        {slidesMarkup}
      </Carousel>
    );
  };

  return (
    <>
      {attachments?.length === 0 ? null : (
        <Box sx={{ width: '100%' }}>
          {attachments.length === 1 ? renderSingleAttachment() : renderMultipleAttachments()}
        </Box>
      )}

      <Lightbox
        open={openPhoto}
        close={() => setOpenPhoto(false)}
        slides={slides}
        index={currentIndex}
        plugins={[Zoom, Counter, Video]}
        counter={{
          container: {
            style: {
              top: 'unset',
              bottom: 10,
              color: 'white',
              fontSize: '16px',
            },
          },
        }}
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInLabel: 'Zoom In',
          zoomOutLabel: 'Zoom Out',
          zoomInButtonAriaLabel: 'Zoom In',
          zoomOutButtonAriaLabel: 'Zoom Out',
        }}
        carousel={{
          finite: true,
          preload: 0,
        }}
        render={{
          buttonPrev: slides.length > 1 ? undefined : () => null,
          buttonNext: slides.length > 1 ? undefined : () => null,
        }}
      />
    </>
  );
};

export default AttachmentGallery;
